import { fmtNum } from '../common/format';
import type { FinanceMonth, Gran } from './types';
import { MONTHS_ABBR } from '../../lib/dates';

// ---- Money formatting ------------------------------------------------------
// Revenue analytics deals in whole euros at thousands scale — grouped, no cents
// (per-booking sums show cents; annual totals don't). Grouping reuses
// the shared fmtNum so the thousands rule lives in one place.
// The sign goes BEFORE the symbol — "−€4.4k", never "€-4.4k". Negative figures
// were unreachable here until adjustments (refunds, credits, arrears carried
// forward) became recordable; they are ordinary now, and a minus wedged between
// the symbol and the digits reads as a typo rather than as money going out.
// A true minus (U+2212), not a hyphen, so it lines up with the digits.
const signed = (v: number, body: string) => (v < 0 ? '−€' : '€') + body;

export function fmtEur(v: number): string {
  return signed(v, fmtNum(Math.abs(Math.round(v))));
}
export function fmtEurShort(v: number): string {
  const a = Math.abs(v);
  if (a >= 1000) return signed(v, (a / 1000).toFixed(a >= 10000 ? 0 : 1) + 'k');
  return signed(v, String(Math.round(a)));
}

// A tidy axis around the data: round the per-tick step to 1/2/2.5/5/10, then
// ceil the max to it (aim ~4 ticks). Shared by every finances chart.
//
// `min` extends the axis BELOW ZERO when the data goes there. Nothing on this
// page produces a negative today — earned revenue cannot be — but the helper is
// shared, and an axis that only ever climbed from zero would draw a negative
// below the canvas or with a negative height rather than visibly wrong.
export function niceScale(v: number, min = 0): { yMin: number; yMax: number; step: number } {
  const lo = Math.min(0, min);
  if (v <= 0 && lo === 0) return { yMin: 0, yMax: 1, step: 1 };
  const raw = Math.max(Math.abs(v), Math.abs(lo)) / 4;
  const mag = Math.pow(10, Math.floor(Math.log10(raw)));
  const n = raw / mag;
  const step = (n <= 1 ? 1 : n <= 2 ? 2 : n <= 2.5 ? 2.5 : n <= 5 ? 5 : 10) * mag;
  return { yMin: Math.floor(lo / step) * step, yMax: Math.max(step, Math.ceil(v / step) * step), step };
}
export function axisTicks(yMax: number, step: number, yMin = 0): number[] {
  const t: number[] = [];
  for (let v = yMin; v <= yMax + step / 2; v += step) t.push(Math.round(v * 100) / 100);
  return t;
}


// The current calendar-month index (0-11) the page anchors "this week / this
// month" to — the live "now".
export const ANCHOR_MONTH = new Date().getMonth();

// ---- Selectors (pure) — operate on the FinanceData the endpoint returns -----
export const monthLabel = (m: number) => MONTHS_ABBR[m]!;
const sumBy = (rows: FinanceMonth[], f: (r: FinanceMonth) => number) => rows.reduce((a, r) => a + f(r), 0);
// THE figure. Value EARNED, spread across the nights actually served — not the
// whole stay landed in its check-in month, which is what made July 2026 report
// €13,710 of August's nights.
//
// A second `received` basis (money by payment date) was built here and removed
// before it shipped (Freddi, 2026-07-30): a page showing two revenue numbers
// asks the reader to know which question they are asking before they can read
// either. The payments ledger still records when money arrives; nothing here
// reports it.
const val = (r: FinanceMonth) => r.booked;

export interface TrendPoint { label: string; revenue: number; wages: number; partial: boolean; forward: boolean }

export function trendPoints(series: FinanceMonth[], scope: number | 'all', gran: Gran): TrendPoint[] {
  const rows = scope === 'all' ? series : series.filter((r) => r.year === scope);
  const years = [...new Set(series.map((r) => r.year))];
  if (gran === 'year') {
    return years.map((y) => {
      const rs = series.filter((r) => r.year === y);
      return { label: String(y), revenue: sumBy(rs, val), wages: sumBy(rs, (r) => r.wages), partial: rs.some((r) => r.partial), forward: rs.some((r) => r.forward) };
    });
  }
  if (gran === 'quarter') {
    const out: Record<string, TrendPoint> = {};
    rows.forEach((r) => {
      const q = Math.floor(r.month / 3);
      const key = scope === 'all' ? `${r.year} Q${q + 1}` : `Q${q + 1}`;
      const p = (out[key] ||= { label: key, revenue: 0, wages: 0, partial: false, forward: false });
      p.revenue += val(r); p.wages += r.wages; p.partial = p.partial || r.partial; p.forward = p.forward || r.forward;
    });
    return Object.values(out);
  }
  return rows.map((r) => ({
    label: scope === 'all' ? (r.month === 0 ? String(r.year) : monthLabel(r.month)) : monthLabel(r.month),
    revenue: val(r), wages: r.wages, partial: r.partial, forward: r.forward,
  }));
}

export function yoyByMonth(series: FinanceMonth[], focusYear: number) {
  return MONTHS_ABBR.map((label, m) => {
    const cur = series.find((r) => r.year === focusYear && r.month === m);
    const prev = series.find((r) => r.year === focusYear - 1 && r.month === m);
    // Forward months haven't been earned — show only the prior-year bar for them.
    return { mi: m, label: label[0]!, full: label, prev: prev ? val(prev) : 0, cur: cur && !cur.forward ? val(cur) : 0 };
  }).filter((m) => m.prev || m.cur);
}

export interface Headline {
  revenue: number; wages: number; net: number; cancelled: number;
  marginPct: number; wagePct: number; yoy: number | null; hasPrevYear: boolean; hasPartial: boolean;
}

export function headline(series: FinanceMonth[], scope: number | 'all', focusYear: number): Headline {
  // "To date" figures exclude the forward book — committed future bookings aren't earned yet.
  const statRows = (scope === 'all' ? series : series.filter((r) => r.year === focusYear)).filter((r) => !r.forward);
  const revenue = sumBy(statRows, val);
  const wages = sumBy(statRows, (r) => r.wages);
  const cancelled = sumBy(statRows, (r) => r.cancelled);
  const focusRows = series.filter((r) => r.year === focusYear);
  // YoY: completed months only (exclude the in-progress month and the forward
  // book), compared against the same window last year.
  const completed = focusRows.filter((r) => !r.partial && !r.forward);
  const months = new Set(completed.map((r) => r.month));
  const prev = series.filter((r) => r.year === focusYear - 1 && months.has(r.month));
  const revCmp = sumBy(completed, val), revPrev = sumBy(prev, val);
  const hasPrevYear = scope !== 'all' && series.some((r) => r.year === focusYear - 1);
  return {
    revenue, wages, net: revenue - wages, cancelled,
    marginPct: revenue ? ((revenue - wages) / revenue) * 100 : 0,
    wagePct: revenue ? (wages / revenue) * 100 : 0,
    yoy: hasPrevYear && revPrev ? (revCmp - revPrev) / revPrev : null,
    hasPrevYear,
    hasPartial: focusRows.some((r) => r.partial),
  };
}
